export class ChatConversationService {
    static async startConversation(conversationStorageKey) {
        try {
            const request = new Request(process.env.CHATPANEL_START_CONVERSATION, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    pageUrl: window.location.href
                })
            });

            const response = await fetch(request);
            if (!response.ok) throw new Error("Failed to start conversation");

            const json = await response.json();
            localStorage.setItem(conversationStorageKey, JSON.stringify(json));
            return json;
        } catch (error) {
            console.error('Error starting conversation:', error);
            throw error;
        }
    }

    static async startConversationWithActionId(conversationStorageKey, triggerActionId) {
        try {
            const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const request = new Request(process.env.CHATPANEL_START_CONVERSATION, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(triggerActionId ?
                    { actionId: `${triggerActionId}`, clientTimeZone, pageUrl: window.location.href } :
                    { clientTimeZone, pageUrl: window.location.href }
                )
            });

            const response = await fetch(request);
            if (!response.ok) throw new Error("Failed to start conversation");

            const json = await response.json();
            localStorage.setItem(conversationStorageKey, JSON.stringify(json));
            return json;
        } catch (error) {
            console.error('Error starting conversation:', error);
            throw error;
        }
    }

    static async resumeConversation(conversationId) {
        const url = `${process.env.CHATPANEL_RESUME_CONVERSATION}?conversation_id=${conversationId}`;

        try {
            const request = new Request(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const response = await fetch(request);
            if (!response.ok) throw new Error("Failed to resume conversation: " + response.statusText);

            return await response.json();
        } catch (error) {
            console.error('Error resuming conversation:', error);
            throw error;
        }
    }

    static getStoredConversation(conversationStorageKey) {
        const conversationContext = localStorage.getItem(conversationStorageKey);
        return conversationContext ? JSON.parse(conversationContext) : null;
    }

    static async sendMessage(conversationId, message) {
        const url = `${process.env.CHATPANEL_SEND_MESSAGE}?conversation_id=${conversationId}`;
        const request = new Request(url, {
            method: process.env.ENVIRONMENT === "PRODUCTION" ? "POST" : "GET",
            headers: {
                "Content-Type": "application/json",
            },
            ...(process.env.ENVIRONMENT === "PRODUCTION" ? {
                body: JSON.stringify({
                    text: message,
                    clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    pageUrl: window.location.href
                })
            } : {}),
        });

        try {
            const response = await fetch(request);
            if (!response.ok) throw new Error("Failed to send message");
            return await response.json();
        } catch (error) {
            console.error('Error sending message:', error);
            throw error;
        }
    }

    static async sendActionId(conversationId, actionId, isFloatingTrigger = false) {
        const endpoint = isFloatingTrigger ?
            process.env.CHATPANEL_SEND_FLOATING_TRIGGER_ACTION :
            process.env.CHATPANEL_SEND_ACTION;
        const url = `${endpoint}?conversation_id=${conversationId}`;

        const request = new Request(url, {
            method: process.env.ENVIRONMENT === "PRODUCTION" ? "POST" : "GET",
            headers: {
                "Content-Type": "application/json",
            },
            ...(process.env.ENVIRONMENT === "PRODUCTION" ? {
                body: JSON.stringify({
                    actionId: actionId,
                    pageUrl: window.location.href,
                    clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                })
            } : {}),
        });

        try {
            const response = await fetch(request);
            if (!response.ok) throw new Error("Failed to send action link");
            return await response.json();
        } catch (error) {
            console.error('Error sending action:', error);
            throw error;
        }
    }

    static async sendExternalLink(conversationId, { id }) {
        const url = `${process.env.CHATPANEL_SEND_EXTERNAL_LINK}?conversation_id=${conversationId}`;
        const request = new Request(url, {
            method: process.env.ENVIRONMENT === "PRODUCTION" ? "POST" : "GET",
            headers: {
                "Content-Type": "application/json",
            },
            ...(process.env.ENVIRONMENT === "PRODUCTION" ? {
                body: JSON.stringify({
                    id: id,
                    pageUrl: window.location.href,
                    clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                })
            } : {}),
        });

        try {
            const response = await fetch(request);
            if (!response.ok) throw new Error("Failed to send external link");
        } catch (error) {
            console.error('Error sending external link:', error);
            throw error;
        }
    }

    static async endConversation(conversationId) {
        const url = `${process.env.CHATPANEL_END_CONVERSATION}?conversation_id=${conversationId}`;
        const request = new Request(url, {
            method: process.env.ENVIRONMENT === "PRODUCTION" ? "POST" : "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        try {
            const response = await fetch(request);
            if (!response.ok) throw new Error("Failed to end conversation");
        } catch (error) {
            console.error('Error ending conversation:', error);
            throw error;
        }
    }

    static async sendMessageFeedback(conversationId, feedback, id) {
        const url = `${process.env.CHATPANEL_SEND_MESSAGE_FEEDBACK}?conversation_id=${conversationId}`;
        const request = new Request(url, {
            method: process.env.ENVIRONMENT === "PRODUCTION" ? "POST" : "GET",
            headers: {
                "Content-Type": "application/json",
            },
            ...(process.env.ENVIRONMENT === "PRODUCTION" ? {
                body: JSON.stringify({
                    feedback,
                    id,
                    pageUrl: window.location.href,
                    clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                })
            } : {}),
        });

        try {
            const response = await fetch(request);
            if (!response.ok) throw new Error("Failed to send feedback");
            return await response.json();
        } catch (error) {
            console.error('Error sending message feedback:', error);
            throw error;
        }
    }

    static async sendConversationFeedback(conversationId, feedbackData, feedbackActionId) {
        const url = `${process.env.CHATPANEL_SEND_CONVERSATION_FEEDBACK}?conversation_id=${conversationId}`;
        const request = new Request(url, {
            method: process.env.ENVIRONMENT === "PRODUCTION" ? "POST" : "GET",
            headers: {
                "Content-Type": "application/json",
            },
            ...(process.env.ENVIRONMENT === "PRODUCTION" ? {
                body: JSON.stringify({
                    ...feedbackData,
                    feedbackActionId,
                    pageUrl: window.location.href,
                    clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                })
            } : {}),
        });

        try {
            const response = await fetch(request);
            if (!response.ok) throw new Error("Failed to send feedback");
            return await response.json();
        } catch (error) {
            console.error('Error sending conversation feedback:', error);
            throw error;
        }
    }

    static async deleteMessageHistory(conversationId) {
        const url = `${process.env.CHATPANEL_DELETE_MESSAGE_HISTORY}?conversation_id=${conversationId}`;
        const request = new Request(url, {
            method: process.env.ENVIRONMENT === "PRODUCTION" ? "POST" : "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        try {
            const response = await fetch(request);
            if (!response.ok) throw new Error("Failed to delete message history");
            return;
        } catch (error) {
            console.error('Error deleting message history:', error);
            throw error;
        }
    }

    static async downloadConversation(conversationId) {
        const url = `${process.env.CHATPANEL_DOWNLOAD_MESSAGE_HISTORY}?conversation_id=${conversationId}`;
        const request = new Request(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        try {
            const response = await fetch(request);
            if (!response.ok) throw new Error("Failed to download message history");

            return new Blob([await response.blob()], {
                type: "text/plain;charset=utf-8",
            });
        } catch (error) {
            console.error('Error downloading conversation:', error);
            throw error;
        }
    }

    static async pollConversationStatus(conversationId, latestResponseId) {
        const url = `${process.env.CHATPANEL_STATUS_POLLING_ENDPOINT}?conversation_id=${conversationId}&latest_response_id=${latestResponseId}`;
        const request = new Request(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        try {
            const response = await fetch(request);
            if (!response.ok) throw new Error("Failed to poll conversation status");
            return await response.json();
        } catch (error) {
            console.error('Error polling conversation status:', error);
            throw error;
        }
    }
}